import { useEffect, useState } from 'react';

export interface Claim {
  analyst: string;
  at: string;
}

interface VerifyState {
  claimed: Record<string, Claim[]>;
  done: Record<string, string[]>;
  score: Record<string, number>;
}

const KEY = 'zenith:verify';
const SCORE_COMPLETE = 25;
const SCORE_FIRST_CLAIM = 10;

function load(): VerifyState {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as VerifyState;
      if (parsed && typeof parsed.claimed === 'object') return parsed;
    }
  } catch {
    /* ignore */
  }
  return { claimed: {}, done: {}, score: {} };
}

export function useVerifyStore() {
  const [state, setState] = useState<VerifyState>(() => load());

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
    } catch {
      /* storage full */
    }
  }, [state]);

  const claims = (postId: string) => state.claimed[postId] ?? [];

  const claim = (postId: string, analyst: string) => {
    if (!analyst.trim()) return false;
    const list = claims(postId);
    if (list.length >= 2 || list.some((c) => c.analyst === analyst)) return false;
    setState((s) => ({
      ...s,
      claimed: { ...s.claimed, [postId]: [...list, { analyst, at: new Date().toISOString() }] },
      score: { ...s.score, [analyst]: (s.score[analyst] ?? 0) + SCORE_FIRST_CLAIM },
    }));
    return true;
  };

  const complete = (postId: string, analyst: string) => {
    const list = claims(postId);
    const done = state.done[postId] ?? [];
    if (!list.some((c) => c.analyst === analyst) || done.includes(analyst)) return false;
    setState((s) => ({
      ...s,
      done: { ...s.done, [postId]: [...(s.done[postId] ?? []), analyst] },
      score: { ...s.score, [analyst]: (s.score[analyst] ?? 0) + SCORE_COMPLETE },
    }));
    return true;
  };

  const resolved = (postId: string) => (state.done[postId] ?? []).length >= 2;

  const leaderboard = Object.entries(state.score).sort((a, b) => b[1] - a[1]);

  return { state, claims, claim, complete, resolved, leaderboard };
}
